import React, { useEffect, useId, useMemo, useState } from 'react';
import { Plus, Trash2, Check, AlertTriangle, TrendingUp, Search } from 'lucide-react';
import { LopSearchableSelect } from '../../components/LopSearchableSelect';
import { LopSearchModal } from '../../components/LopSearchModal';
import { useIndices } from '../../hooks/useIndices';
import { getIndex4Funds, saveIndex4Funds } from '../../api/index4funds';
import { LopButton } from '../../components/LopButton';
import { t } from '../../i18n/t';
import './FundIndicesPanel.css';
import { showErrorToast } from '../../components/ErrorToast';

interface FundIndicesPanelProps {
  fundId: number;
}

// Local editing row — percent is a 0-100 number (converted to a fraction only on
// save). idxId null until the operator picks an index.
interface EditRow {
  idxId: number | null;
  percent: number | '';
}

const EPS = 0.01; // percent tolerance for the "sums to 100" rule

// Editable "tracked indices" sub-section of the fund form. A fund may track 0/1/
// several indices; when it tracks any, the weights must sum to 100% (blocked on
// save + shown live). Replace-all save via PUT /api/index4funds/{fundId}.
export function FundIndicesPanel({ fundId }: FundIndicesPanelProps) {
  const titleId = useId();
  const { data: indices } = useIndices();

  const [rows, setRows] = useState<EditRow[]>([]);
  // row index whose index is being picked in the magnifier modal (the inline dropdown is clipped by the panel)
  const [pickerRow, setPickerRow] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [status, setStatus] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setStatus(null);
    getIndex4Funds(fundId)
      .then((data) => {
        if (cancelled) return;
        setRows(data.map((d) => ({ idxId: d.idxId, percent: round2(d.percentInFund * 100) })));
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fundId]);

  const idxOptions = useMemo(
    () => (indices ?? []).map((i) => ({ id: i.idxId, name: i.idxName })),
    [indices],
  );

  const total = useMemo(
    () => rows.reduce((s, r) => s + (typeof r.percent === 'number' ? r.percent : 0), 0),
    [rows],
  );

  // Save is valid when: no rows (fund tracks nothing) OR every row is complete,
  // ids are distinct, and the weights sum to 100%.
  const validity = useMemo(() => {
    if (rows.length === 0) return { ok: true, reason: '' as string };
    if (rows.some((r) => r.idxId == null)) return { ok: false, reason: t('fundIndices.errPickIndex') };
    if (rows.some((r) => !(typeof r.percent === 'number') || r.percent <= 0))
      return { ok: false, reason: t('fundIndices.errPositive') };
    const ids = rows.map((r) => r.idxId);
    if (new Set(ids).size !== ids.length) return { ok: false, reason: t('fundIndices.errDuplicate') };
    if (Math.abs(total - 100) > EPS) {
      const diff = round2(Math.abs(100 - total));
      return { ok: false, reason: total < 100 ? t('fundIndices.errShort', { diff }) : t('fundIndices.errOver', { diff }) };
    }
    return { ok: true, reason: '' };
  }, [rows, total]);

  const setRow = (i: number, patch: Partial<EditRow>) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((prev) => [...prev, { idxId: null, percent: '' }]);
  const removeRow = (i: number) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!validity.ok || saving) return;
    setSaving(true);
    setStatus(null);
    try {
      const payload = rows.map((r) => ({ idxId: r.idxId as number, percentInFund: (r.percent as number) / 100 }));
      const saved = await saveIndex4Funds(fundId, payload);
      setRows(saved.map((d) => ({ idxId: d.idxId, percent: round2(d.percentInFund * 100) })));
      setStatus({ kind: 'success', text: t('fundIndices.saved') });
    } catch (err: unknown) {
      showErrorToast(err, { title: t('errorToast.titleSave') });
    } finally {
      setSaving(false);
    }
  };

  const totalOk = rows.length > 0 && Math.abs(total - 100) <= EPS;

  return (
    <section className="lop-fip" aria-labelledby={titleId}>
      <div className="lop-fip__header">
        <h2 id={titleId} className="lop-fip__title">{t('fundIndices.panelTitle')}</h2>
        <button type="button" className="lop-fip__add" onClick={addRow} disabled={loading}>
          <Plus size={16} aria-hidden="true" />
          <span>{t('fundIndices.addIndex')}</span>
        </button>
      </div>

      {loading && <div className="lop-fip__loading">{t('common.loading')}</div>}
      {loadError && <div className="lop-fip__error" role="alert">{t('fundIndices.loadError', { message: loadError })}</div>}

      {!loading && !loadError && rows.length === 0 && (
        <div className="lop-fip__empty" role="status">
          <TrendingUp size={26} className="lop-fip__empty-icon" aria-hidden="true" />
          <div className="lop-fip__empty-headline">{t('fundIndices.emptyHeadline')}</div>
          <div className="lop-fip__empty-helper">{t('fundIndices.emptyHelper')}</div>
        </div>
      )}

      {!loading && !loadError && rows.length > 0 && (
        <div className="lop-fip__table-wrap">
          <table className="lop-fip__table">
            <thead>
              <tr>
                <th scope="col" className="lop-fip__col-idx">{t('fundIndices.columnIndex')}</th>
                <th scope="col" className="lop-fip__col-pct">{t('fundIndices.columnPercent')}</th>
                <th scope="col" className="lop-fip__col-act" aria-label={t('fundIndices.columnActions')} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>
                    <div className="lop-fip__idx-picker">
                      <LopSearchableSelect
                        value={r.idxId}
                        options={idxOptions}
                        onChange={(id) => setRow(i, { idxId: id })}
                        placeholder={t('fundIndices.pickIndexPlaceholder')}
                      />
                      <button
                        type="button"
                        className="lop-fip__idx-pick"
                        aria-label={t('fundIndices.searchIndex')}
                        title={t('fundIndices.searchIndex')}
                        onClick={() => setPickerRow(i)}
                      >
                        <Search size={16} />
                      </button>
                    </div>
                  </td>
                  <td className="lop-fip__pct-cell">
                    <input
                      type="number"
                      className="lop-fip__pct-input"
                      dir="ltr"
                      min={0}
                      max={100}
                      step="any"
                      value={r.percent}
                      onChange={(e) => setRow(i, { percent: e.target.value === '' ? '' : Number(e.target.value) })}
                      aria-label={t('fundIndices.columnPercent')}
                    />
                    <span className="lop-fip__pct-sign" aria-hidden="true">%</span>
                  </td>
                  <td className="lop-fip__act-cell">
                    <button
                      type="button"
                      className="lop-fip__remove"
                      onClick={() => removeRow(i)}
                      aria-label={t('fundIndices.removeRow')}
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className={`lop-fip__total-row ${totalOk ? 'is-ok' : 'is-bad'}`}>
                <td className="lop-fip__total-label">{t('fundIndices.total')}</td>
                <td className="lop-fip__total-value" dir="ltr">
                  {round2(total)}%
                  {totalOk ? (
                    <Check size={15} className="lop-fip__total-icon" aria-hidden="true" />
                  ) : (
                    <AlertTriangle size={15} className="lop-fip__total-icon" aria-hidden="true" />
                  )}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Live validity + save-result message — announced to screen readers. */}
      <div className="lop-fip__footer">
        <div className="lop-fip__msg" aria-live="polite">
          {!validity.ok && <span className="lop-fip__msg-error" role="alert">{validity.reason}</span>}
          {status?.kind === 'success' && <span className="lop-fip__msg-success">{status.text}</span>}
          {status?.kind === 'error' && <span className="lop-fip__msg-error" role="alert">{status.text}</span>}
        </div>
        <LopButton
          variant="primary"
          className="lop-fip__save"
          onClick={handleSave}
          loading={saving}
          disabled={!validity.ok || loading}
        >
          {t('fundIndices.save')}
        </LopButton>
      </div>
      {pickerRow !== null && (
        <LopSearchModal
          title={t('fundIndices.searchIndex')}
          table="INDICES"
          columns={[
            { field: 'idxId', headerName: t('idx.columns.idxId') },
            { field: 'idxName', headerName: t('idx.columns.idxName') },
            { field: 'symbol', headerName: t('idx.columns.symbol') },
            { field: 'taseIdxId', headerName: t('idx.columns.taseIdxId') },
          ]}
          onSelect={(row) => {
            const picked = row.idxId;
            if (typeof picked === 'number') setRow(pickerRow, { idxId: picked });
          }}
          onClose={() => setPickerRow(null)}
        />
      )}
    </section>
  );
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
